import React, { useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import {
  Box, Chip, IconButton, Tooltip, Avatar, Drawer, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import InboxIcon from '@mui/icons-material/Inbox';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import GroupHeader from './group-header';
import GroupSendBar from './group-send-bar';
import '../chat/styles.css';

// F4 — Conversa em GRUPO. Subtree ISOLADO do 1:1 (early-return em components/chat/index.js).
// FASE MOCK: header/send-bar espelham o 1:1; participantes/solicitacoes/thread mockados pra validar UX.
const PRIMARY = 'var(--mexx-green-primary, #8BC34A)';
const ROLE = { CUSTOMER: 1, AGENT: 2 };
const ST = { INVITED: 1, JOINREQ: 2, ACTIVE: 3, LEFT: 4, REMOVED: 5 };
const NAME_COLORS = ['#e5679a', '#277afe', '#7c4dff', '#00897b', '#f4511e', '#3949ab', '#00acc1', '#c2185b'];
const colorForName = (n) => NAME_COLORS[Math.abs([...(n || 'x')].reduce((a, c) => a + c.charCodeAt(0), 0)) % NAME_COLORS.length];

const MOCK_PARTICIPANTS = [
  { id: 1, role: ROLE.AGENT, status: ST.ACTIVE, name: 'Ana Ribeiro', isAdmin: true },
  { id: 2, role: ROLE.AGENT, status: ST.ACTIVE, name: 'Carlos Dias' },
  { id: 3, role: ROLE.CUSTOMER, status: ST.ACTIVE, name: 'Maria Souza', phone: '5581988801122' },
  { id: 4, role: ROLE.CUSTOMER, status: ST.ACTIVE, name: 'Joao Lima', phone: '5581988803344' },
  { id: 5, role: ROLE.CUSTOMER, status: ST.LEFT, name: 'Pedro Alves', phone: '5581988805566' },
];
const MOCK_JOINREQS = [
  { id: 101, joinRequestId: 'JR-1', name: 'Lucas Melo', phone: '5581988807788' },
  { id: 102, joinRequestId: 'JR-2', name: 'Bianca Rocha', phone: '5581988809900' },
];
const MOCK_MESSAGES = [
  { id: 1, type: 'system', text: 'Maria Souza entrou no grupo' },
  { id: 2, mine: false, author: 'Maria Souza', text: 'Bom dia! Alguem pode me ajudar com o pedido #4821?', at: '09:12' },
  { id: 3, mine: true, author: 'Ana', text: 'Oi Maria! Ja estou verificando seu pedido.', at: '09:13', status: 'read' },
  { id: 4, mine: false, author: 'Joao Lima', text: 'Eu tambem estou com uma duvida parecida.', at: '09:15' },
  { id: 5, type: 'system', text: 'Pedro Alves saiu do grupo' },
  { id: 6, mine: true, author: 'Carlos', text: 'Joao, ja te respondo em seguida.', at: '09:16', status: 'delivered' },
];

function Ticks(props) {
  if (props.status === 'read') return <DoneAllIcon sx={{ fontSize: 15, color: '#34b7f1' }} />;
  if (props.status === 'delivered') return <DoneAllIcon sx={{ fontSize: 15, color: '#8aa' }} />;
  return <DoneIcon sx={{ fontSize: 15, color: '#8aa' }} />;
}

function GroupConversation(props) {
  const { intl, isMobile = false } = props;
  const chat = props.chats?.chat || {};
  const t = (id, v) => intl.formatMessage({ id }, v);

  // Grupo real usa chat.groupname; no modo debug (sobre uma conversa 1:1) NAO cai no nome do cliente
  // — forca um nome de grupo ficticio pra dar ideia de grupo.
  const groupName = chat.groupname || t('groupconv.mock.groupname');
  const isSuspended = !!chat.issuspended;

  const [participants, setParticipants] = useState(MOCK_PARTICIPANTS);
  const [joinReqs, setJoinReqs] = useState(MOCK_JOINREQS);
  const [messages, setMessages] = useState(MOCK_MESSAGES);
  const [inviteLink, setInviteLink] = useState(chat.invitelink || 'https://chat.whatsapp.com/MOCKINVITE01');
  const [panel, setPanel] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '' });

  const toast = (msg) => setSnack({ open: true, msg });
  const activeCount = participants.filter((p) => p.status === ST.ACTIVE).length;
  const agents = participants.filter((p) => p.role === ROLE.AGENT);
  const customers = participants.filter((p) => p.role === ROLE.CUSTOMER);

  const copyInvite = () => { if (inviteLink) { navigator.clipboard.writeText(inviteLink); toast(t('groupconv.copied')); } };
  const resetInvite = () => { setInviteLink('https://chat.whatsapp.com/MOCKINVITE02NEW'); toast(t('groupconv.resetinvite.done')); };
  const doRemoveCustomer = (p) => { setParticipants((l) => l.map((x) => (x.id === p.id ? { ...x, status: ST.REMOVED } : x))); setConfirm(null); toast(t('groupconv.removed.done')); };
  const doDeleteGroup = () => { setConfirm(null); toast(t('groupconv.deleted.done')); };
  const approveJoin = (r) => { setJoinReqs((l) => l.filter((x) => x.id !== r.id)); setParticipants((l) => [...l, { id: r.id, role: ROLE.CUSTOMER, status: ST.ACTIVE, name: r.name, phone: r.phone }]); toast(t('groupconv.approved.done')); };
  const rejectJoin = (r) => { setJoinReqs((l) => l.filter((x) => x.id !== r.id)); toast(t('groupconv.rejected.done')); };
  const sendMsg = (text) => setMessages((l) => [...l, { id: l.length + 1000, mine: true, author: t('groupconv.you'), text, at: '--:--', status: 'sent' }]);

  const statusChip = (status) => {
    const map = {
      [ST.ACTIVE]: { id: 'groupconv.status.active', bg: '#e8f5e9', c: '#2e7d32' },
      [ST.JOINREQ]: { id: 'groupconv.status.joinrequested', bg: '#fff8e1', c: '#f57f17' },
      [ST.INVITED]: { id: 'groupconv.status.invited', bg: '#e3f2fd', c: '#1565c0' },
      [ST.LEFT]: { id: 'groupconv.status.left', bg: '#eceff1', c: '#607d8b' },
      [ST.REMOVED]: { id: 'groupconv.status.removed', bg: '#ffebee', c: '#c62828' },
    };
    const s = map[status] || map[ST.LEFT];
    return <Chip size="small" label={t(s.id)} sx={{ bgcolor: s.bg, color: s.c, fontWeight: 600, height: 20 }} />;
  };

  const width = (!isMobile || (isMobile && !props.chats?.issidebaropen)) ? (isMobile ? '100%' : '75%') : '100%';

  return (
    <div className="omnichannel-chat" style={{ width, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <GroupHeader
        intl={intl} chat={chat} isMobile={isMobile}
        groupName={groupName} memberCount={activeCount} joinReqCount={joinReqs.length}
        isSuspended={isSuspended} inviteLink={inviteLink}
        onOpenParticipants={() => setPanel('participants')}
        onOpenJoinRequests={() => setPanel('joinrequests')}
        onReset={resetInvite} onDelete={() => setConfirm({ type: 'delete' })}
        onCopyLink={copyInvite} onMock={(m) => toast(m)}
      />

      {/* ===== THREAD (fundo e bolhas iguais ao 1:1: #F3F3F3 / sender #A8C8D6 / receiver #CDDFE7) ===== */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '1% 2%', bgcolor: '#F3F3F3', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {messages.map((m) => {
          if (m.type === 'system') {
            return (
              <Box key={m.id} sx={{ textAlign: 'center', my: 0.5 }}>
                <span style={{ background: '#e6e6e6', color: '#666', fontSize: 12, padding: '3px 10px', borderRadius: 8 }}>{m.text}</span>
              </Box>
            );
          }
          const nameColor = m.mine ? '#2a6a3a' : colorForName(m.author);
          return (
            <Box key={m.id} sx={{ display: 'flex', justifyContent: m.mine ? 'flex-end' : 'flex-start', gap: 0.75 }}>
              {!m.mine && <Avatar sx={{ width: 28, height: 28, bgcolor: nameColor, fontSize: 13, mt: 0.5 }}>{(m.author || '?').charAt(0)}</Avatar>}
              <Box sx={{ maxWidth: '68%', p: '6px 10px', borderRadius: 2, bgcolor: m.mine ? '#A8C8D6' : '#CDDFE7', boxShadow: '0 1px 0.5px rgba(0,0,0,.13)' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: nameColor, marginBottom: 2 }}>{m.author}</div>
                <div style={{ fontSize: 14, color: '#222', whiteSpace: 'pre-wrap' }}>{m.text}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 2 }}>
                  <span style={{ fontSize: 11, color: '#999' }}>{m.at}</span>
                  {m.mine && <Ticks status={m.status} />}
                </div>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ===== SEND BAR ===== */}
      {isSuspended ? (
        <Box sx={{ p: '12px 16px', borderTop: '1px solid #e0e0e0', bgcolor: '#fff8e1', color: '#f57f17', fontSize: 13, display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('groupconv.suspended.hint')}
        </Box>
      ) : (
        <GroupSendBar intl={intl} disabled={false} onSend={sendMsg} onMock={(m) => toast(m)} />
      )}

      {/* ===== DRAWER (participantes / solicitacoes) =====
          O override global (styles/mexx-global-override.css) pinta o PRIMEIRO FILHO <div> do
          .MuiDrawer-paperAnchorRight de VERDE + texto BRANCO, e forca todos os button/IconButton a
          branco. Por isso: (1) o filho direto e um <section> (o seletor `> div:first-child` deixa de
          casar -> some o fundo verde e o texto branco herdado); (2) o PaperProps restaura icones
          (escuros) e o botao Rejeitar (outlined -> vermelho). Aprovar (contained) fica branco sobre
          verde, que e o correto. Mesma solucao do chat-user-details-sidebar. */}
      <Drawer anchor="right" open={!!panel} onClose={() => setPanel(null)}
        PaperProps={{ sx: {
          width: { xs: '100%', sm: 380 },
          backgroundColor: '#fff',
          '& .MuiIconButton-root': { color: 'rgba(0,0,0,0.54) !important' },
          '& .MuiButton-outlined': { color: '#d32f2f !important', borderColor: '#d32f2f !important' },
        } }}>
        <section style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontWeight: 600, fontSize: 16, flex: 1, color: '#222' }}>
              {panel === 'joinrequests' ? t('groupconv.panel.joinrequests') : t('groupconv.panel.participants')}
            </span>
            <IconButton size="small" onClick={() => setPanel(null)}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: '8px 0', bgcolor: '#fff' }}>
            {panel === 'participants' && (
              <>
                <SectionLabel icon={<SupportAgentIcon sx={{ fontSize: 16, color: PRIMARY }} />} text={t('groupconv.section.agents')} />
                {agents.map((p) => (
                  <RowPerson key={p.id} name={p.name} sub={t('groupconv.role.internal')}
                    rightChip={p.isAdmin ? <Chip size="small" icon={<StarIcon />} label={t('groupconv.admin')} sx={{ height: 20, bgcolor: '#fff3e0', color: '#e65100', '& .MuiChip-icon': { color: '#e65100' } }} /> : null} />
                ))}

                <SectionLabel icon={<PeopleAltIcon sx={{ fontSize: 16, color: PRIMARY }} />} text={t('groupconv.section.customers')} />
                {customers.length === 0 && <EmptyState text={t('groupconv.empty.participants')} />}
                {customers.map((p) => (
                  <RowPerson key={p.id} name={p.name} sub={p.phone} rightChip={statusChip(p.status)}
                    action={p.status === ST.ACTIVE ? (
                      <Tooltip title={t('groupconv.remove')}>
                        <IconButton size="small" onClick={() => setConfirm({ type: 'remove', target: p })}>
                          <PersonRemoveIcon fontSize="small" sx={{ color: '#c62828' }} />
                        </IconButton>
                      </Tooltip>
                    ) : null} />
                ))}
              </>
            )}

            {panel === 'joinrequests' && (
              <>
                {joinReqs.length === 0 && <EmptyState text={t('groupconv.empty.joinrequests')} />}
                {joinReqs.map((r) => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: colorForName(r.name), fontSize: 14 }}>{(r.name || '?').charAt(0)}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#222' }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{r.phone}</div>
                    </Box>
                    <Button size="small" variant="contained" onClick={() => approveJoin(r)}
                      sx={{ bgcolor: PRIMARY, color: '#fff', '&:hover': { bgcolor: PRIMARY, filter: 'brightness(0.95)' }, minWidth: 0, px: 1.5 }}>{t('groupconv.approve')}</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => rejectJoin(r)} sx={{ minWidth: 0, px: 1.5 }}>{t('groupconv.reject')}</Button>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </section>
      </Drawer>

      {/* ===== CONFIRM DIALOG ===== */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 420 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirm?.type === 'delete' ? t('groupconv.delete.confirm.title') : t('groupconv.remove.confirm.title')}
        </DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 14, color: '#555' }}>
            {confirm?.type === 'delete' ? t('groupconv.delete.confirm.text') : t('groupconv.remove.confirm.text', { name: confirm?.target?.name || '' })}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: '8px 16px 16px' }}>
          <Button onClick={() => setConfirm(null)}>{t('groupconv.cancel')}</Button>
          <Button variant="contained" color="error"
            onClick={() => (confirm?.type === 'delete' ? doDeleteGroup() : doRemoveCustomer(confirm.target))}>
            {confirm?.type === 'delete' ? t('groupconv.menu.delete') : t('groupconv.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </div>
  );
}

function SectionLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, pt: 1.5, pb: 0.5, color: '#999', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {props.icon}{props.text}
    </Box>
  );
}

function RowPerson(props) {
  const { name, sub, rightChip, action } = props;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '8px 16px', bgcolor: '#fff', '&:hover': { bgcolor: '#fafafa' } }}>
      <Avatar sx={{ width: 34, height: 34, bgcolor: colorForName(name), fontSize: 14 }}>{(name || '?').charAt(0)}</Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#222' }}>{name}</div>
        <div style={{ fontSize: 12, color: '#888' }}>{sub}</div>
      </Box>
      {rightChip}
      {action}
    </Box>
  );
}

function EmptyState(props) {
  return (
    <Box sx={{ textAlign: 'center', p: '32px 16px', color: '#bbb' }}>
      <InboxIcon sx={{ fontSize: 48 }} />
      <div style={{ fontSize: 14, color: '#999', marginTop: 8 }}>{props.text}</div>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  chats: state.chats,
  userSession: state.userSession,
});

export default injectIntl(connect(mapStateToProps)(GroupConversation));
